import { h, type CSSProperties } from 'preact';

declare global {
  interface Window {
    ICONSET_CACHE_BUSTER?: number | string;
  }
}

interface OrthomolIconProps {
  /**
   * The name of the icon from the iconset
   * @example 'delivery-van-24', 'cross-24', 'arrow-24'
   */
  name: string;

  /**
   * The size of the icon
   * @default '24'
   */
  size?: string;

  /**
   * Rotation angle in degrees
   * @default 0
   */
  rotation?: number;

  /**
   * Custom viewBox for the SVG
   * @example '0 0 48 24'
   */
  viewBox?: string;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Additional inline styles
   */
  style?: CSSProperties;

  /**
   * ARIA label for accessibility
   */
  ariaLabel?: string;

  /**
   * Whether the icon is purely decorative (hides from screen readers)
   */
  ariaHidden?: boolean;
}

export const OrthomolIcon = ({
  name,
  size = '24',
  rotation = 0,
  viewBox,
  className,
  style,
  ariaLabel,
  ariaHidden = false,
}: OrthomolIconProps) => {
  if (!name) {
    return null;
  }

  const attributes: Record<string, string | number | undefined> = {
    'data-size': size,
  };

  if (rotation && rotation !== 0) {
    attributes['data-rotate'] = rotation;
  }

  if (viewBox) {
    attributes.viewBox = viewBox;
    const viewBoxParts = viewBox.split(' ');
    if (viewBoxParts.length >= 3) {
      attributes.width = viewBoxParts[2];
    }
  }

  const iconsetPath = '/assets/iconset.svg';

  // get cache buster timestamp from window.ICONSET_CACHE_BUSTER (set by PHP in _head.html.twig via iconset_cache_buster() function)
  const cacheBuster =
    typeof window !== 'undefined' && window.ICONSET_CACHE_BUSTER ? window.ICONSET_CACHE_BUSTER : Date.now();

  const iconsetUrlPath = `/cache-buster-${cacheBuster}${iconsetPath}`;

  return (
    <svg
      className={className}
      style={style}
      data-size={attributes['data-size'] as string}
      data-rotate={attributes['data-rotate'] as number}
      viewBox={attributes.viewBox as string}
      width={attributes.width as string}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden || !ariaLabel}
      role={ariaLabel ? 'img' : undefined}
    >
      <use xlinkHref={`${iconsetUrlPath}#${name}`} />
    </svg>
  );
};
